import {
  addDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
} from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';

interface DatePickerProps {
  label?: string;
  required?: boolean;
  value?: string;
  onChange: (date: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
  minDate?: string;
  maxDate?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  required = false,
  value = '',
  onChange,
  placeholder = 'Select date',
  disabled = false,
  error,
  helperText,
  className = '',
  minDate,
  maxDate,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ? new Date(value) : null);
  const containerRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  const maxDateObj = maxDate ? new Date(maxDate) : addDays(today, 365);

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setDropdownPosition(spaceBelow < 300 && spaceAbove > 300 ? 'top' : 'bottom');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDateSelect = (date: Date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (dateOnly < todayOnly || (maxDateObj && date > maxDateObj)) {
      return;
    }

    setSelectedDate(date);
    onChange(format(date, 'yyyy-MM-dd'));
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1));
  };

  const getDaysInMonth = () => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start, end });

    // Add empty cells for days before the first day of the month
    const startDay = start.getDay();
    const emptyDays = Array(startDay).fill(null);

    return [...emptyDays, ...days];
  };

  const isDateDisabled = (date: Date | null) => {
    if (!date) return true;
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dateOnly < todayOnly || (maxDateObj && date > maxDateObj);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium mb-1 text-[#621C43]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Input Field */}
      <div className="relative">
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          min={minDate}
          max={maxDate}
          disabled={disabled}
          placeholder={placeholder}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`
            w-full px-3 py-[6px] pr-10
            border border-gray-300
            rounded-lg 
            bg-[#4A082D33] 
            text-[#4A082D]
            focus:outline-none 
            focus:ring-2 
            focus:ring-[#621C43] 
            focus:ring-opacity-50
            disabled:opacity-50 
            disabled:cursor-not-allowed
            ${error ? 'border-red-500' : ''}
            [&::-webkit-calendar-picker-indicator]:opacity-0
            [&::-webkit-calendar-picker-indicator]:pointer-events-none
          `}
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
        >
          <svg
            className={`w-4 h-4 text-[#621C43] transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Calendar Dropdown */}
      {isOpen && !disabled && (
        <div
          className={`absolute ${
            dropdownPosition === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          } left-0 w-full bg-white border border-[#4A082D] rounded-lg shadow-lg z-50`}
        >
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-2 border-b border-[#4A082D33]">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-2 hover:bg-[#4A082D11] rounded-lg transition-colors"
            >
              <svg
                className="w-4 h-4 text-[#621C43]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <h3 className="text-lg font-semibold text-[#621C43]">
              {format(currentMonth, 'MMMM yyyy')}
            </h3>

            <button
              type="button"
              onClick={handleNextMonth}
              className="p-2 hover:bg-[#4A082D11] rounded-lg transition-colors"
            >
              <svg
                className="w-4 h-4 text-[#621C43]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Days of Week */}
          <div className="grid grid-cols-7 gap-1 p-1">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
              <div key={day} className="p-1 text-center text-xs font-medium text-[#621C43]">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1 p-1">
            {getDaysInMonth().map((date, index) => {
              if (!date) {
                return <div key={index} className="p-2" />;
              }

              const isSelected = selectedDate && isSameDay(date, selectedDate);
              const isCurrentDay = isToday(date);
              const isCurrentMonth = isSameMonth(date, currentMonth);
              const isDisabled = isDateDisabled(date);

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDateSelect(date)}
                  disabled={isDisabled}
                  className={`
                    p-1 text-xs rounded-lg transition-all duration-200
                    ${
                      isSelected
                        ? 'bg-[#621C43] text-white font-semibold'
                        : isCurrentDay
                          ? 'bg-[#4A082D22] text-[#621C43] font-semibold border border-[#621C43]'
                          : isCurrentMonth
                            ? 'text-[#4A082D] hover:bg-[#4A082D11]'
                            : 'text-[#4A082D66]'
                    }
                    ${isDisabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  {format(date, 'd')}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Helper Text */}
      {helperText && !error && <p className="text-sm text-[#621C43] mt-1">{helperText}</p>}

      {/* Error Message */}
      {error && <p className="text-sm text-red-500 font-medium mt-1">{error}</p>}
    </div>
  );
};

export default DatePicker;
